import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Phone, Car, MapPin, Headphones, ShieldCheck,
  Gauge, Clock, Award, CalendarDays, Truck, Gem, Mountain, Caravan, Container, Zap,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { BRAND } from "../lib/brand";

const FEATURES = [
  { icon: Car, title: "Moderní vozový park", text: "Pravidelně servisovaná a čistá vozidla." },
  { icon: MapPin, title: "Vyzvednutí po celé ČR", text: "Možnost přistavení vozu dle dohody." },
  { icon: Headphones, title: "Podpora kdykoliv", text: "Rádi vám poradíme a pomůžeme." },
  { icon: ShieldCheck, title: "Žádné skryté poplatky", text: "Transparentní ceny bez překvapení." },
];

const VALUE_STRIP = [
  { icon: Gauge, title: "RYCHLE", text: "Rychlé vyřízení a okamžitá rezervace." },
  { icon: ShieldCheck, title: "BEZPEČNĚ", text: "Pojištěná vozidla a férové podmínky." },
  { icon: Clock, title: "NONSTOP", text: "Jsme tu pro vás 24 hodin denně." },
  { icon: Award, title: "FLEXIBILITA", text: "Individuální přístup a férové ceny." },
];

const CATEGORY_IMG = {
  osobni: "/images/cat-osobni.jpg",
  dodavky: "/images/cat-dodavky.jpg",
  luxusni: "/images/cat-luxusni.jpg",
};

const CATEGORY_ICON = {
  osobni: Car, dodavky: Truck, luxusni: Gem, suv: Mountain,
  sportovni: Gauge, obytne: Caravan, voziky: Container, elektro: Zap,
};

export default function Home() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  const priceOf = (slug) => cats.find((c) => c.slug === slug)?.price_from;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-black overflow-hidden min-h-[540px] lg:min-h-[640px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="/images/hero-wide.jpg"
            alt="KAME vozidlo"
            className="w-full h-full object-cover object-right"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20" />
        </div>

        <div className="container-kame relative z-10 py-16 lg:py-24 w-full">
          <motion.div
            className="max-w-xl text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="font-display text-white uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
              Půjč si svobodu.
              <br />
              <span className="gold">Kamkoli. Kdykoli.</span>
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg">
              Rychle, bezpečně a nonstop.
              <br />
              Kvalita, spolehlivost a flexibilita.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/vozovy-park" className="btn-gold" data-testid="hero-cta-button">
                Zjistit více <ArrowRight size={18} />
              </Link>
              <Link to="/rezervace" className="btn-outline-gold" data-testid="hero-reserve-button">
                <CalendarDays size={18} /> Rezervovat
              </Link>
            </div>

            <div className="mt-8 bg-black/60 backdrop-blur-sm border border-[#B58A50]/30 rounded-sm p-4 flex items-center gap-4 max-w-md">
              <span className="w-11 h-11 flex items-center justify-center rounded-full bg-[#B58A50]/15 text-[#B58A50] shrink-0">
                <Phone size={20} />
              </span>
              <div>
                <a href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`} className="font-display text-xl text-white tracking-wide">
                  {BRAND.phones[0]}
                </a>
                <p className="text-white/60 text-xs">Mimo uvedenou pracovní dobu po předchozí domluvě kdykoliv.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="bg-[#0a0a0a] border-y border-[#1a1a1a]">
        <div className="container-kame py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="border-l-2 border-[#B58A50]/40 pl-4">
              <f.icon className="text-[#B58A50] mb-3" size={30} />
              <h3 className="font-display text-lg text-white uppercase leading-tight">{f.title}</h3>
              <p className="text-white/55 text-sm mt-2">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[#f5f3ef] py-16 lg:py-20">
        <div className="container-kame">
          <div className="text-center mb-12">
            <p className="eyebrow">Vyber si své vozidlo</p>
            <h2 className="font-display text-3xl sm:text-4xl text-black uppercase mt-2">Široká nabídka vozidel</h2>
            <div className="w-16 h-1 bg-[#B58A50] mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cats.map((c, i) => {
              const Icon = CATEGORY_ICON[c.slug] || Car;
              const img = c.image_url ? mediaUrl(c.image_url) : CATEGORY_IMG[c.slug];
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                  className="bg-white border border-black/5 rounded-sm p-6 flex flex-col shadow-sm hover:shadow-lg transition-shadow duration-200"
                  data-testid={`home-category-${c.slug}`}
                >
                  <h3 className="font-display text-2xl text-black uppercase">{c.name}</h3>
                  <p className="text-black/55 text-sm mt-1">{c.description}</p>
                  <div className="my-5 h-32 flex items-center justify-center overflow-hidden">
                    {img ? (
                      <img src={img} alt={c.name} className={c.image_url ? "w-full h-full object-cover rounded-sm" : "w-full h-full object-contain"} />
                    ) : (
                      <Icon size={64} strokeWidth={1.4} className="text-[#B58A50]" />
                    )}
                  </div>
                  <p className="text-black/40 text-xs mb-3">od {c.price_from.toLocaleString("cs-CZ")} Kč / den</p>
                  <Link
                    to={`/vozovy-park?kategorie=${c.slug}`}
                    className="mt-auto bg-black text-white hover:bg-[#B58A50] hover:text-black transition-colors duration-200 text-center py-3 text-xs uppercase tracking-wider font-semibold rounded-sm flex items-center justify-center gap-2"
                    data-testid={`home-category-link-${c.slug}`}
                  >
                    Zobrazit nabídku <ArrowRight size={14} />
                  </Link>
                </motion.div>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-black rounded-sm p-7 flex flex-col justify-center"
            >
              <CalendarDays className="text-[#B58A50] mb-4" size={32} />
              <h3 className="font-display text-2xl text-white uppercase leading-tight">Rezervuj online</h3>
              <p className="text-white/60 text-sm mt-2">Rychle a jednoduše kdykoliv.</p>
              <Link to="/rezervace" className="btn-gold mt-6 justify-center" data-testid="home-reserve-cta">
                Rezervace <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="bg-black py-14">
        <div className="container-kame grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUE_STRIP.map((v) => (
            <div key={v.title} className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-full border border-[#B58A50]/40 flex items-center justify-center text-[#B58A50] shrink-0">
                <v.icon size={22} />
              </span>
              <div>
                <h3 className="font-display text-lg text-white uppercase">{v.title}</h3>
                <p className="text-white/55 text-sm mt-1">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="relative bg-[#0a0a0a] border-t border-[#1a1a1a] py-16">
        <div className="container-kame text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase">Připraveni vyrazit na cestu?</h2>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">
            Vyberte si vůz z naší nabídky a rezervujte online během pár minut. Rádi vám poradíme na telefonu {BRAND.phones[0]}.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/vozovy-park" className="btn-gold" data-testid="cta-fleet-btn">
              Vozový park <ArrowRight size={18} />
            </Link>
            <Link to="/kontakt" className="btn-outline-gold" data-testid="cta-contact-btn">
              Kontaktujte nás
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
